<template>
  <b-modal
    id="tax-configuration-group-sku-modal"
    v-model="showModal"
    :title="modalTitle"
    centered
    size="xl"
    no-close-on-backdrop
    hide-footer
    @hidden="cleanState"
  >
    <e-spinner v-if="fetching" />

    <div v-else>
      <b-alert
        v-if="group.ncm"
        variant="info"
        show
        class="p-1 mb-1"
      >
        {{ $t('TAX_CONFIGURATION_GROUP.SKU_ASSOCIATION.NCM_RESTRICTED', { ncm: group.ncm }) }}
      </b-alert>

      <e-filters
        :title="$t('Filtrar produtos')"
        :search-text="$t('Pesquisar e adicionar')"
        :searching="searching"
        @search="searchAndAdd"
        @reset="resetFilters"
      >
        <b-row>
          <b-col md="3">
            <FormulateInput
              id="sku-modal-ean"
              v-model="filters.ean"
              type="text"
              :label="$t('Código de barras')"
              :placeholder="$t('Código de barras')"
            />
          </b-col>
          <b-col md="5">
            <FormulateInput
              id="sku-modal-name"
              v-model="filters.name"
              type="text"
              :label="$t('Nome')"
              :placeholder="$t('Nome')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="sku-modal-ncm"
              v-model="filters.ncm"
              type="select-searchable"
              :label="$t('NCM')"
              :placeholder="$t('Digite para pesquisar')"
              :options="getComboNcms"
              :disabled="!!group.ncm"
              @fetchSearch="ncmSearch"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4">
            <FormulateInput
              id="sku-modal-brand"
              v-model="filters.brand"
              type="vue-select"
              :label="$t('Marca')"
              :placeholder="$t('Todas')"
              :options="getComboBrands"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="sku-modal-group"
              v-model="filters.group"
              type="vue-select"
              :label="$t('Grupo')"
              :placeholder="$t('Todos')"
              :options="getComboGroups"
              @input="selectGroup"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="sku-modal-subgroup"
              v-model="filters.subgroup"
              type="vue-select"
              :label="$t('Subgrupo')"
              :placeholder="$t('Todos')"
              :options="getComboSubgroups"
              :disabled="!filters.group"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4">
            <FormulateInput
              id="sku-modal-department"
              v-model="filters.department"
              type="vue-select"
              :label="$t('Departamento')"
              :placeholder="$t('Todos')"
              :options="getComboDepartments"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="sku-modal-line"
              v-model="filters.line"
              type="vue-select"
              :label="$t('Linha')"
              :placeholder="$t('Todas')"
              :options="getComboLines"
            />
          </b-col>
        </b-row>
      </e-filters>

      <b-card-actions
        no-actions
        :title="associatedTitle"
        :show-loading="saving"
      >
        <b-row class="mb-1 align-items-end">
          <b-col md="6">
            <FormulateInput
              id="sku-modal-local-search"
              v-model="localSearch"
              type="text"
              :label="$t('Localizar na lista')"
              :placeholder="$t('Nome ou código de barras')"
            />
          </b-col>
          <b-col
            md="6"
            class="text-right"
          >
            <e-page-size-selector
              class="d-inline-block mr-1"
              :per-page="localPaging.pageSize"
              @change="pageSizeChange"
            />
            <e-button
              v-if="notAssociatedSkus.length"
              class="mr-1"
              variant="outline-warning"
              icon="x-circle"
              :text="removeNotAssociatedText"
              :title="$t('Mantém na lista apenas os produtos já associados a esta exceção')"
              @click="removeNotAssociated"
            />
            <e-button
              v-if="selectedSkus.length"
              variant="outline-danger"
              icon="trash"
              :text="$t('Remover todos')"
              @click="removeAll"
            />
          </b-col>
        </b-row>

        <b-table
          show-empty
          responsive
          striped
          small
          class="bordered"
          :empty-text="
            $t('Nenhum produto associado. Use os filtros acima para pesquisar e adicionar.')
          "
          :fields="fields"
          :items="paginatedSkus"
        >
          <template #cell(action)="{ item }">
            <e-grid-actions
              :show-update="false"
              :show-delete="true"
              @delete="removeSku(item)"
            />
          </template>

          <template #cell(taxConfigurationGroupName)="{ item }">
            <b-badge
              v-if="isFromAnotherGroup(item)"
              variant="warning"
              :title="$t('Ao salvar, o produto passará a usar esta exceção')"
            >
              {{ item.taxConfigurationGroupName }}
            </b-badge>
            <b-badge
              v-else-if="isFromSameGroup(item)"
              variant="success"
              :title="$t('Já está associado a esta exceção')"
            >
              {{ $t('Associado') }}
            </b-badge>
            <span v-else>-</span>
          </template>
        </b-table>

        <b-row>
          <b-col cols="6">
            <e-pagination-summary
              :current-page="localPaging.currentPage"
              :per-page="localPaging.pageSize"
              :total="filteredSkus.length"
              :total-on-page="paginatedSkus.length"
            />
          </b-col>
          <b-col cols="6">
            <b-pagination
              v-model="localPaging.currentPage"
              align="right"
              :total-rows="filteredSkus.length"
              :per-page="localPaging.pageSize"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-row class="mt-1">
        <b-col
          cols="12"
          align="right"
        >
          <e-button
            class="mr-1"
            variant="outline-primary"
            icon="x-circle"
            :text="$t('Cancelar')"
            @click="showModal = false"
          />
          <e-button
            variant="primary"
            icon="save"
            :busy="saving"
            :text="$t('Salvar')"
            @click="save"
          />
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BRow, BCol, BTable, BPagination, BAlert, BBadge } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapGetters } from 'vuex'
import {
  EButton,
  EFilters,
  EGridActions,
  EPageSizeSelector,
  EPaginationSummary,
  ESpinner,
} from '@/views/components'

/** Acima disso, pedimos confirmação antes de despejar o resultado inteiro na lista. */
const LARGE_RESULT_THRESHOLD = 300

const getInitialFilters = () => ({
  ean: '',
  name: '',
  ncm: null,
  brand: null,
  group: null,
  subgroup: null,
  department: null,
  line: null,
})

export default {
  name: 'TaxConfigurationGroupSkuModal',
  components: {
    BModal,
    BRow,
    BCol,
    BTable,
    BPagination,
    BAlert,
    BBadge,
    BCardActions,
    EButton,
    EFilters,
    EGridActions,
    EPageSizeSelector,
    EPaginationSummary,
    ESpinner,
  },

  data() {
    return {
      showModal: false,
      fetching: false,
      searching: false,
      saving: false,
      group: {},
      /** Snapshot do servidor: base do diff no save. */
      originalSkus: [],
      selectedSkus: [],
      filters: getInitialFilters(),
      localSearch: '',
      localPaging: {
        currentPage: 1,
        pageSize: 10,
      },
    }
  },

  computed: {
    ...mapGetters('pages/catalog/products', [
      'getComboBrands',
      'getComboGroups',
      'getComboSubgroups',
      'getComboDepartments',
      'getComboLines',
    ]),
    ...mapGetters('common/ncms', ['getComboNcms']),

    modalTitle() {
      return `${this.$t('Associar produtos')} - ${this.group.name || ''}`
    },

    associatedTitle() {
      return `${this.$t('Produtos associados')} (${this.selectedSkus.length})`
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '80px' },
        },
        {
          label: this.$t('EAN'),
          key: 'ean',
          class: 'text-center',
          thStyle: { width: '140px' },
          formatter: value => value || '-',
        },
        { label: this.$t('Nome'), key: 'name' },
        {
          label: this.$t('NCM'),
          key: 'ncm',
          class: 'text-center',
          thStyle: { width: '110px' },
          formatter: value => value || '-',
        },
        {
          label: this.$t('Marca'),
          key: 'brandName',
          thStyle: { width: '160px' },
          formatter: value => value || '-',
        },
        {
          label: this.$t('Exceção atual'),
          key: 'taxConfigurationGroupName',
          class: 'text-center',
          thStyle: { width: '180px' },
        },
      ]
    },

    filteredSkus() {
      const search = this.localSearch.trim().toLowerCase()
      if (!search) return this.selectedSkus

      return this.selectedSkus.filter(
        sku =>
          (sku.name || '').toLowerCase().includes(search) || String(sku.ean || '').includes(search)
      )
    },

    paginatedSkus() {
      const { currentPage, pageSize } = this.localPaging
      return this.filteredSkus.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    },

    /**
     * O que a gravação incluiria: produtos sem exceção nenhuma e produtos que hoje pertencem a
     * outro cadastro. Uma pesquisa ampla enche a lista com eles, e este é o desfazer.
     */
    notAssociatedSkus() {
      return this.selectedSkus.filter(sku => !this.isFromSameGroup(sku))
    },

    removeNotAssociatedText() {
      return `${this.$t('Remover não associados')} (${this.notAssociatedSkus.length})`
    },
  },

  watch: {
    // Remover itens pode deixar a página atual vazia; volta para uma página que ainda existe.
    filteredSkus(list) {
      const lastPage = Math.max(1, Math.ceil(list.length / this.localPaging.pageSize))
      if (this.localPaging.currentPage > lastPage) this.localPaging.currentPage = lastPage
    },
  },

  methods: {
    ...mapActions('pages/catalog/products', [
      'fetchProductCombos',
      'setComboSubgroups',
      'searchProducts',
    ]),
    ...mapActions('pages/invoice/taxConfigurationGroups', [
      'fetchSkusByGroupId',
      'saveTaxConfigurationGroupSkus',
    ]),
    ...mapActions('common/ncms', ['fetchNcms']),

    async show(group) {
      this.cleanState()
      this.group = { ...group }
      this.showModal = true

      try {
        this.fetching = true
        await Promise.all([this.fetchProductCombos(), this.loadAssociatedSkus()])

        // O NCM do cadastro trava o filtro; o combo é por busca, então precisa ser carregado.
        if (this.group.ncm) {
          await this.fetchNcms({ itemSearch: this.group.ncm })
          this.filters.ncm = this.group.ncm
        }
      } catch (error) {
        this.showGenericError({ error })
      } finally {
        this.fetching = false
      }
    },

    async loadAssociatedSkus() {
      // O endpoint devolve só os SKUs deste cadastro, então não repete a FK em cada linha. Sem
      // carimbá-la aqui, um produto já associado ficaria indistinguível de um sem exceção nenhuma.
      const results = (await this.fetchSkusByGroupId(this.group.id)).map(sku => ({
        ...sku,
        taxConfigurationGroupId: this.group.id,
        taxConfigurationGroupName: this.group.name,
      }))

      this.originalSkus = results
      this.selectedSkus = [...results]
    },

    async ncmSearch(loading, search) {
      try {
        loading(true)
        await this.fetchNcms({ itemSearch: search })
      } catch (e) {
        this.showError({ message: this.$t('Não foi possível fazer a pesquisa') })
      } finally {
        loading(false)
      }
    },

    selectGroup() {
      this.filters.subgroup = null
      this.setComboSubgroups(this.filters.group)
    },

    async searchAndAdd() {
      try {
        this.searching = true

        const data = await this.searchProducts({
          ean: this.filters.ean || null,
          name: this.filters.name || null,
          ncm: this.filters.ncm || null,
          brandId: this.filters.brand,
          groupId: this.filters.group,
          subgroupId: this.filters.subgroup,
          departmentId: this.filters.department,
          lineId: this.filters.line,
          active: true,
        })

        const results = data.results || []

        if (!results.length) {
          this.showWarning({ message: this.$t('Nenhum produto encontrado para os filtros informados') })
          return
        }

        if (results.length > LARGE_RESULT_THRESHOLD) {
          const confirmed = await this.confirm({
            text: this.$t('TAX_CONFIGURATION_GROUP.SKU_ASSOCIATION.CONFIRM_LARGE_RESULT', {
              count: results.length,
            }),
          })
          if (!confirmed) return
        }

        // A associação é por SKU: sem ele não há como montar o payload, e os itens colapsariam
        // num só na deduplicação.
        if (results.some(product => !product.skus?.[0]?.id)) {
          this.showError({
            message: this.$t('A pesquisa não retornou o identificador do SKU dos produtos'),
          })
          return
        }

        this.addSkus(results)
      } catch (error) {
        this.showGenericError({ error })
      } finally {
        this.searching = false
      }
    },

    addSkus(products) {
      const existingIds = new Set(this.selectedSkus.map(sku => sku.skuId))
      const added = []
      let duplicated = 0

      products.forEach(product => {
        const skuId = product.skus[0].id

        if (existingIds.has(skuId)) {
          duplicated += 1
          return
        }
        existingIds.add(skuId)
        added.push(this.toProductRow(product))
      })

      this.selectedSkus = [...this.selectedSkus, ...added]

      this.showSuccess({
        message: this.$t('TAX_CONFIGURATION_GROUP.SKU_ASSOCIATION.ADDED_SUMMARY', {
          added: added.length,
          duplicated,
        }),
      })
    },

    /**
     * Achata a linha de /api/products no mesmo formato do endpoint de SKUs do grupo, que já vem
     * plano. A associação é por SKU, e a tela trabalha com um SKU por produto.
     */
    toProductRow(product) {
      const [sku] = product.skus

      return {
        skuId: sku.id,
        productId: product.id,
        name: product.name,
        ean: product.ean,
        ncm: product.tax?.ncmCode ?? null,
        brandName: product.brand?.name ?? null,
        taxConfigurationGroupId: sku.taxConfigurationGroupId ?? null,
        taxConfigurationGroupName: sku.taxConfigurationGroupName ?? null,
      }
    },

    isFromAnotherGroup(sku) {
      return !!sku.taxConfigurationGroupId && sku.taxConfigurationGroupId !== this.group.id
    },
    isFromSameGroup(sku) {
      return !!sku.taxConfigurationGroupId && sku.taxConfigurationGroupId === this.group.id
    },

    removeSku(sku) {
      this.selectedSkus = this.selectedSkus.filter(item => item.skuId !== sku.skuId)
    },

    /** Desfaz o que a pesquisa trouxe, preservando o que já está gravado no cadastro. */
    async removeNotAssociated() {
      const confirmed = await this.confirm({
        text: this.$t('TAX_CONFIGURATION_GROUP.SKU_ASSOCIATION.REMOVE_NOT_ASSOCIATED_CONFIRM', {
          count: this.notAssociatedSkus.length,
        }),
      })
      if (!confirmed) return

      this.selectedSkus = this.selectedSkus.filter(sku => this.isFromSameGroup(sku))
    },

    async removeAll() {
      const confirmed = await this.confirm({
        text: this.$t('Todos os produtos serão desassociados desta exceção. Deseja continuar?'),
      })
      if (!confirmed) return

      this.selectedSkus = []
    },

    pageSizeChange(pageSize) {
      this.localPaging.pageSize = pageSize
      this.localPaging.currentPage = 1
    },

    resetFilters() {
      this.filters = getInitialFilters()
      // O NCM do cadastro não é escolha do usuário: continua travado após limpar.
      if (this.group.ncm) this.filters.ncm = this.group.ncm
      this.setComboSubgroups(null)
    },

    async save() {
      const originalIds = new Set(this.originalSkus.map(sku => sku.skuId))
      const currentIds = new Set(this.selectedSkus.map(sku => sku.skuId))

      const added = [...currentIds].filter(id => !originalIds.has(id))
      const deleted = [...originalIds].filter(id => !currentIds.has(id))

      if (!added.length && !deleted.length) {
        this.showModal = false
        return
      }

      try {
        this.saving = true
        await this.saveTaxConfigurationGroupSkus({ id: this.group.id, added, deleted })

        this.showSuccess({ message: this.$t('Atualização realizada com sucesso') })
        this.$emit('saved')
        this.showModal = false
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    cleanState() {
      this.group = {}
      this.originalSkus = []
      this.selectedSkus = []
      this.filters = getInitialFilters()
      this.localSearch = ''
      this.localPaging = { currentPage: 1, pageSize: 10 }
      this.fetching = false
      this.searching = false
      this.saving = false
    },
  },
}
</script>

<style scoped></style>
